import { useCallback, useRef, useEffect, useMemo } from 'react';
import { API } from '../utils/api';

/**
 * useXHR - Custom hook for all API calls via XMLHttpRequest.
 * Bypasses the platform's fetch interception that causes XHR queue congestion.
 *
 * Usage:
 *   const { get, post, put, del, poll } = useXHR(token);
 *   get('/categories', { onSuccess: (data) => setCategories(data) });
 *   post('/search', { body: { query: 'test' }, onSuccess: ... });
 *   poll('/task-status/123', { interval: 2000, until: (d) => d.status === 'completed', onDone: ... });
 */
const useXHR = (token) => {
  const mountedRef = useRef(true);
  const pollRefs = useRef([]);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pollRefs.current.forEach(clearInterval);
      pollRefs.current = [];
    };
  }, []);

  const request = useCallback((method, path, options = {}) => {
    const {
      body, onSuccess, onError, onFinally, timeout = 60000, headers = {}
    } = options;
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${API}${path}`, true);
      if (tokenRef.current) xhr.setRequestHeader('Authorization', `Bearer ${tokenRef.current}`);
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      if (body && !isFormData) xhr.setRequestHeader('Content-Type', 'application/json');
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.timeout = timeout;
      
      xhr.onload = function() {
        if (!mountedRef.current) return;
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch(e) { data = xhr.responseText; }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          onSuccess && onSuccess(data);
          resolve(data);
        } else {
          const errMsg = data?.detail || `Error ${xhr.status}`;
          onError && onError(errMsg, data);
          reject(errMsg);
        }
        onFinally && onFinally();
      };
      xhr.onerror = function() {
        if (!mountedRef.current) return;
        const errMsg = 'Network error';
        onError && onError(errMsg);
        onFinally && onFinally();
        reject(errMsg);
      };
      xhr.ontimeout = function() {
        if (!mountedRef.current) return;
        const errMsg = 'Request timeout';
        onError && onError(errMsg);
        onFinally && onFinally();
        reject(errMsg);
      };
      xhr.send(body ? (isFormData ? body : JSON.stringify(body)) : null);
    });
  }, []); // Stable - uses tokenRef instead of token

  const get = useCallback((path, opts) => request('GET', path, opts), [request]);
  const post = useCallback((path, opts) => request('POST', path, opts), [request]);
  const put = useCallback((path, opts) => request('PUT', path, opts), [request]);
  const del = useCallback((path, opts) => request('DELETE', path, opts), [request]);

  /**
   * poll - Repeatedly GET an endpoint until a condition is met.
   * @param {string} path - API path (e.g., '/task-status/abc123')
   * @param {Object} opts
   * @param {number} opts.interval - Ms between polls (default 2000)
   * @param {Function} opts.until - (data) => bool. Stop when true.
   * @param {Function} opts.onDone - Called with final data when until() returns true.
   * @param {Function} opts.onProgress - Called with each poll response.
   * @param {Function} opts.onError - Called if a poll indicates failure.
   * @param {number} opts.maxDuration - Max ms to poll (default 300000 = 5min).
   * @returns {Function} cancel - Call to stop polling early.
   */
  const poll = useCallback((path, opts = {}) => {
    const {
      interval = 2000, until, onDone, onProgress, onError, maxDuration = 300000
    } = opts;
    
    const startTime = Date.now();
    const id = setInterval(() => {
      if (!mountedRef.current) { clearInterval(id); return; }
      if (Date.now() - startTime > maxDuration) {
        clearInterval(id);
        pollRefs.current = pollRefs.current.filter(x => x !== id);
        onError && onError('Polling timeout exceeded');
        return;
      }
      
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API}${path}`, true);
      if (tokenRef.current) xhr.setRequestHeader('Authorization', `Bearer ${tokenRef.current}`);
      xhr.onload = function() {
        if (!mountedRef.current) { clearInterval(id); return; }
        try {
          const data = JSON.parse(xhr.responseText);
          onProgress && onProgress(data);
          if (until && until(data)) {
            clearInterval(id);
            pollRefs.current = pollRefs.current.filter(x => x !== id);
            onDone && onDone(data);
          }
        } catch(e) {}
      };
      xhr.send();
    }, interval);
    
    pollRefs.current.push(id);
    
    // Return cancel function
    return () => {
      clearInterval(id);
      pollRefs.current = pollRefs.current.filter(x => x !== id);
    };
  }, []); // Stable - uses tokenRef

  const setMounted = useCallback((val) => { mountedRef.current = val; }, []);

  return useMemo(() => ({ get, post, put, del, request, poll, setMounted }), [get, post, put, del, request, poll, setMounted]);
};

export default useXHR;
